import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Lock, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-beige-50">
      <Header />
      <main className="pt-20">
        <article className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="glass-strong rounded-3xl p-8 md:p-12 mb-8">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-10 h-10 text-brown-700" />
                <h1 className="text-4xl md:text-5xl font-bold text-brown-900">Privacy Policy</h1>
              </div>
              <p className="text-lg text-brown-700">
                Last Updated: 02.12.2025
              </p>
            </div>

            <div className="space-y-8">
              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Introduction</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Sterling Spins ("we," "our," or "us") is committed to protecting your privacy and 
                    ensuring the security of your personal information. This Privacy Policy explains how 
                    we collect, use, disclose, and safeguard your information when you visit our website 
                    sterling-spins.com. By using our website, you consent to the data practices described 
                    in this policy.
                  </p>
                  <p>
                    We operate as an independent review and comparison platform for UK online casinos. 
                    This policy applies to all information collected through our website, including 
                    information you provide directly and data collected automatically through your use 
                    of our services.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <FileText className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Information We Collect</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Personal Information</h3>
                    <p>
                      When you contact us through our contact form or email, we may collect personal 
                      information including your name, email address, and any message content you provide. 
                      This information is used solely to respond to your inquiries and provide customer 
                      support services.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Automatically Collected Information</h3>
                    <p>
                      We automatically collect certain information when you visit our website, including 
                      your IP address, browser type, device information, operating system, pages viewed, 
                      time spent on pages, referring website addresses, and browsing patterns. This 
                      information helps us understand how visitors use our website and improve our services.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Cookies and Tracking Technologies</h3>
                    <p>
                      We use cookies and similar tracking technologies to collect information about your 
                      browsing behavior. Cookies are small data files stored on your device that help us 
                      recognize you, remember your preferences, and enhance your experience. For detailed 
                      information about our cookie usage, please refer to our Cookie Policy.
                    </p>
                  </div>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">How We Use Your Information</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    We use the information we collect for various purposes, including providing and 
                    maintaining our website services, responding to your inquiries and support requests, 
                    analyzing website usage patterns to improve content and user experience, sending 
                    promotional communications if you have opted in to receive them, detecting and 
                    preventing fraud or abuse, and complying with legal obligations.
                  </p>
                  <p>
                    We do not sell, rent, or trade your personal information to third parties. However, 
                    we may share information with service providers who assist us in operating our 
                    website and conducting our business, such as web hosting providers, analytics 
                    services, and email service providers. These service providers are contractually 
                    obligated to maintain the confidentiality of your information.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Affiliate Relationships</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Sterling Spins operates as an independent review platform and may receive compensation 
                    through affiliate relationships with online casinos featured on our website. When you 
                    click on links to casinos or register with casino operators through our website, 
                    these operators may collect information about your visit or registration.
                  </p>
                  <p>
                    We are not responsible for the privacy practices of third-party casino operators. 
                    Each casino operates under its own privacy policy and data protection practices. 
                    We encourage you to review the privacy policies of any casino you choose to register 
                    with to understand how they collect, use, and protect your information.
                  </p>
                  <p>
                    Our affiliate relationships do not involve sharing your personal information with 
                    casino operators. We only receive compensation when you click through to casino 
                    websites or complete registrations, but we do not transmit your personal data to 
                    these operators as part of our affiliate relationships.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Lock className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Data Security</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    We implement appropriate technical and organizational security measures to protect 
                    your personal information against unauthorized access, alteration, disclosure, or 
                    destruction. These measures include secure server infrastructure, encryption of data 
                    transmissions, regular security assessments, and access controls limiting employee 
                    access to personal information.
                  </p>
                  <p>
                    While we strive to protect your personal information, no method of transmission over 
                    the internet or electronic storage is completely secure. We cannot guarantee absolute 
                    security of your information, but we are committed to maintaining reasonable security 
                    measures appropriate to the nature of the information we collect and store.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Your Rights Under UK GDPR</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Under UK General Data Protection Regulation (UK GDPR), you have several rights 
                    regarding your personal information:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Right to Access:</strong> You can request copies of your personal information we hold.</li>
                    <li><strong>Right to Rectification:</strong> You can request correction of inaccurate or incomplete information.</li>
                    <li><strong>Right to Erasure:</strong> You can request deletion of your personal information in certain circumstances.</li>
                    <li><strong>Right to Restrict Processing:</strong> You can request limitation of how we use your information.</li>
                    <li><strong>Right to Object:</strong> You can object to processing of your personal information.</li>
                    <li><strong>Right to Data Portability:</strong> You can request transfer of your information to another service.</li>
                    <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, you can withdraw it at any time.</li>
                  </ul>
                  <p>
                    To exercise any of these rights, please contact us at info@sterling-spins.com. 
                    We will respond to your request within one month, though this may be extended in 
                    complex cases. We may require verification of your identity before processing requests 
                    to protect your privacy and security.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Data Retention</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    We retain your personal information only for as long as necessary to fulfill the 
                    purposes outlined in this Privacy Policy, unless a longer retention period is 
                    required or permitted by law. When determining retention periods, we consider the 
                    amount, nature, and sensitivity of the information, potential risk from unauthorized 
                    access, and applicable legal requirements.
                  </p>
                  <p>
                    Contact form submissions and email communications are typically retained for two years 
                    following the last interaction, unless longer retention is necessary for legal or 
                    business purposes. Automatically collected analytics data is typically retained for 
                    up to 26 months in aggregated form that does not identify individual users.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Children's Privacy</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Our website is intended for individuals aged 18 and over only. We do not knowingly 
                    collect personal information from children under 18 years of age. If we become aware 
                    that we have collected personal information from a child under 18, we will take steps 
                    to delete such information promptly.
                  </p>
                  <p>
                    If you are a parent or guardian and believe your child has provided us with personal 
                    information, please contact us immediately at info@sterling-spins.com so we can take 
                    appropriate action.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">International Data Transfers</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Your information may be transferred to and processed in countries outside the United 
                    Kingdom, including countries within the European Economic Area and other jurisdictions. 
                    When we transfer information internationally, we ensure appropriate safeguards are in 
                    place to protect your information in accordance with UK data protection laws.
                  </p>
                  <p>
                    We use service providers located in various countries, and these transfers are 
                    governed by standard contractual clauses approved by UK data protection authorities 
                    or other appropriate safeguards that ensure your information receives adequate protection.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Changes to This Privacy Policy</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    We may update this Privacy Policy from time to time to reflect changes in our 
                    practices, technology, legal requirements, or other factors. We will notify you of 
                    any material changes by posting the updated policy on this page with a revised "Last 
                    Updated" date.
                  </p>
                  <p>
                    Your continued use of our website after changes become effective constitutes 
                    acceptance of the revised Privacy Policy. We encourage you to review this policy 
                    periodically to stay informed about how we protect your information.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Contact Us</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    If you have questions, concerns, or requests regarding this Privacy Policy or our 
                    data practices, please contact us:
                  </p>
                  <div className="bg-brown-100 rounded-lg p-6">
                    <p className="font-semibold text-brown-900 mb-2">Sterling Spins</p>
                    <p>Email: info@sterling-spins.com</p>
                    <p>Phone: +44 0973 385 308</p>
                    <p>Address: London, United Kingdom</p>
                  </div>
                  <p>
                    For complaints about our data handling practices, you have the right to lodge a 
                    complaint with the Information Commissioner's Office (ICO), the UK's data protection 
                    supervisory authority.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

